// sendUnlockMessage.js
'use strict';
import { replace } from 'redux-first-history';

import fetch from '../resource/customFetch.js';
import { getHeaders } from '../resource/fetchOptionHeader.js';
import getResourceUrl from '../resource/getResourceUrl.js';
import handleFetchError from '../resource/handleFetchError.js';
import { getKeySystem } from '../resource/getDrmInfo.js';
import getCurrentUnixTimestamp from '../resource/getCurrentUnixTimestamp.js';
import {
  CONFIG_PRIORITY_PRESENCE_USER,
  CONFIG_PRIORITY_PRESENCE_CLIENT,
} from '../resource/configPriority.js';
import getCurrentLanguage from '../resource/getCurrentLanguage.js';

import getMeData from '../selector/getMeData.js';
import getMessageData from '../selector/getMessageData.js';
import getHasEnoughBalanceForUnlock from '../selector/getHasEnoughBalanceForUnlock.js';
import getRouterData from '../selector/getRouterData.js';
import getTimestampOffset, {
  Accuracy,
} from '../selector/getTimestampOffset.js';
import getAbTestingData from '../selector/getAbTestingData.js';
import getMessageUnlockPrice from '../selector/getMessageUnlockPrice.js';
import { getIsDrmRequirementMatched } from '../selector/getDrmKeySystemAndRobustness.js';
import getOperationData from '../selector/getOperationData.js';

import toggleModal from '../action/toggleModal.js';
import addModal from '../action/addModal.js';
import login from '../action/login.js';
import fetchDrmLicenseAvailability from '../action/fetchDrmLicenseAvailability.js';
import addvalue from '../action/addvalue.js';
import pushToastr from '../action/pushToastr.js';
import deductDiamond from '../action/deductDiamond.js';
import toggleDrmNotSupportAlert from '../action/toggleDrmNotSupportAlert.js';
import fetchOembedData from '../action/fetchOembedData.js';

import {
  SET_NETWORKING_FETCHING,
  SET_NETWORKING_SUCCESS,
  SET_NETWORKING_ERROR,
} from '../ActionTypes.js';

const freezoneDetailReg = /\/freezone\/[a-z\d]+/i; // TODO: remote config

/**
 * Send unlock message
 * @kind action
 * @param {string} {id} - message id.
 * @param {DrmFeatureName} {drmFeatureName} - drm feature name.
 * @param {boolean} {shouldCheckDrm} - should check drm.
 * @param {boolean} {shouldOpenMessagePackUpsellingModal} - should open message pack upselling modal.
 * @return {Promise} Action promise.
 */
const sendUnlockMessage =
  ({
    id,
    shouldCheckDrm = false,
    shouldOpenMessagePackUpsellingModal = false,
    drmFeatureName,
  }) =>
  async (dispatch, getState) => {
    const token = getMeData(getState(), 'token');
    if (!token) {
      return dispatch(login());
    }

    if (shouldCheckDrm) {
      const checkDrmCapability = (
        await import('../action/checkDrmCapability.js')
      ).default;
      await dispatch(checkDrmCapability({ isEarlyLeave: true }));
      const isMatched = getIsDrmRequirementMatched(getState(), drmFeatureName);
      if (!isMatched) {
        return dispatch(
          toggleDrmNotSupportAlert({
            actionName: 'sendUnlockMessage',
            actionPayload: {
              id,
              shouldCheckDrm: false,
              drmFeatureName,
            },
          })
        );
      }
    }

    const abPriority = getMeData(getState(), 'id')
      ? CONFIG_PRIORITY_PRESENCE_USER
      : CONFIG_PRIORITY_PRESENCE_CLIENT;
    const abToken = getAbTestingData(
      getState(),
      undefined,
      'abToken',
      abPriority
    );

    if (abToken) {
      const abTokenExp = getAbTestingData(
        getState(),
        undefined,
        'exp',
        abPriority
      );
      const timestampOffsetSeconds =
        getTimestampOffset(getState(), Accuracy.SECOND) || 0;
      const now = getCurrentUnixTimestamp({
        offsetSeconds: timestampOffsetSeconds,
      });

      if (now > abTokenExp) {
        return dispatch(addModal({ id: 'PriceRefreshModal' }));
      }
    }

    const hasEnoughBalance = getHasEnoughBalanceForUnlock(getState(), id);

    if (!hasEnoughBalance) {
      dispatch(addvalue({ via: 'unlock-message' }));
      return dispatch(
        pushToastr({
          textKey: 'alert_insufficient_funds',
          color: 'error',
        })
      );
    }

    const ctaUrlData = getMessageData(getState(), id, 'ctaUrlData');
    if (shouldOpenMessagePackUpsellingModal && ctaUrlData) {
      await dispatch(
        fetchOembedData({
          url: ctaUrlData.ctaUrl,
          language: getCurrentLanguage(),
        })
      );
      const oembedData = getOperationData(
        getState(),
        ['oembed'],
        ctaUrlData.ctaUrl
      );
      if (oembedData) {
        return dispatch(
          toggleModal({
            modalId: 'MessagePackUpsellingModal',
            payload: {
              messageId: id,
              productId: ctaUrlData.productId,
              drmFeatureName,
            },
            isOpen: true,
          })
        );
      }
    }

    const selectPath = ['messages', id, 'unlock'];

    const url = getResourceUrl({ endpoint: `/messages/${id}/unlock` });
    const fetchOptions = {
      method: 'PUT',
      headers: {
        ...getHeaders(),
        Authorization: `Bearer ${token}`,
      },
    };

    dispatch({ type: SET_NETWORKING_FETCHING, payload: { selectPath } });

    try {
      const unlockPrice = getMessageUnlockPrice(getState(), id);

      if (unlockPrice <= 0) {
        throw new Error('The free message cannot be unlocked');
      }

      let response = await fetch(url.href, fetchOptions);

      if (!response.ok) {
        response = await handleFetchError({
          response,
          dispatch,
          getState,
          fetchOptions,
          fetchUrl: url,
        });
      }

      dispatch(deductDiamond({ price: unlockPrice }));
      dispatch({ type: SET_NETWORKING_SUCCESS, payload: { selectPath } });
      await dispatch(
        fetchDrmLicenseAvailability({
          messageId: id,
          keySystem: getKeySystem(),
        })
      );

      const isOnFreeZoneDetail = freezoneDetailReg.test(
        getRouterData(getState(), 'location')?.pathname
      );
      const isUnlocked = getMessageData(getState(), id, 'canViewMedia');

      if (isOnFreeZoneDetail && isUnlocked) {
        dispatch(replace(`/post/${id}`));
      }
    } catch (error) {
      let errorObject = {};
      try {
        errorObject = JSON.parse(error.message);
        // eslint-disable-next-line no-empty
      } catch (_) {}

      if (errorObject.status === 403) {
        dispatch(
          pushToastr({
            textKey: 'something_went_wrong_block',
            color: 'error',
          })
        );
      }
      return dispatch({
        type: SET_NETWORKING_ERROR,
        payload: { selectPath, error },
      });
    }
  };

export default sendUnlockMessage;
